// ============================================================
// ByOurConsent.com -- Login Page
// ============================================================

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const LoginPage = () => {
    const { login } = useAuth();
    const navigate  = useNavigate();
    const location  = useLocation();
    const [form,    setForm]    = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState('');
    const from = location.state?.from || location.state?.from?.pathname || '/';

    const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            await login(form.email, form.password);
            toast.success('Welcome back!');
            navigate(from, { replace: true });
        } catch (err) {
            const msg = err.message || 'Login failed. Please try again.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet><title>Sign In — By Our Consent</title></Helmet>

            {/* Mini nav */}
            <div style={{ background:'#1e2a3a', height:'56px', display:'flex', alignItems:'center', padding:'0 1.5rem', justifyContent:'space-between' }}>
                <Link to="/" style={{ color:'#e8dfc8', fontSize:'17px', fontWeight:600, fontFamily:"'EB Garamond',Georgia,serif", textDecoration:'none', display:'flex', alignItems:'center', gap:'9px' }}>
                    <svg width="20" height="24" viewBox="0 0 28 32" fill="none"><path d="M14 1L2 6v10c0 8.5 5.2 15.4 12 17.4C20.8 31.4 26 24.5 26 16V6L14 1z" fill="#8b1a1a" stroke="#c8beaa" strokeWidth="0.8"/><circle cx="14" cy="17" r="3" fill="none" stroke="#e8dfc8" strokeWidth="1.2"/><line x1="14" y1="12" x2="14" y2="14" stroke="#e8dfc8" strokeWidth="1.2"/></svg>
                    By Our Consent
                </Link>
                <Link to="/register" style={{ color:'#b8ad98', fontSize:'12px', fontFamily:'sans-serif', textDecoration:'none', letterSpacing:'0.06em', textTransform:'uppercase' }}>
                    Create account
                </Link>
            </div>

            {/* Page */}
            <div style={{ minHeight:'calc(100vh - 56px)', background:'#f5f0e8', display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem 1rem' }}>
                <div style={{ width:'100%', maxWidth:'420px' }}>

                    {/* Header */}
                    <div style={{ textAlign:'center', marginBottom:'2rem' }}>
                        <div style={{ fontSize:'10px', fontFamily:'sans-serif', letterSpacing:'0.16em', textTransform:'uppercase', color:'#8b6a3a', marginBottom:'6px' }}>Welcome back</div>
                        <h1 style={{ fontSize:'28px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond',Georgia,serif", marginBottom:'8px' }}>Sign in</h1>
                        <div style={{ width:'36px', height:'2px', background:'#8b1a1a', margin:'0 auto' }} />
                    </div>

                    {/* Card */}
                    <div style={{ background:'#ede8dc', border:'0.5px solid #c8beaa', borderRadius:'4px', padding:'2rem' }}>
                        {error && (
                            <div style={{ background:'#fdf0ee', border:'0.5px solid #d4a8a0', borderRadius:'3px', padding:'10px 12px', marginBottom:'1rem', fontSize:'13px', fontFamily:'sans-serif', color:'#8b1a1a' }}>
                                {error}
                            </div>
                        )}
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom:'1rem' }}>
                                <label style={{ display:'block', fontSize:'11px', fontFamily:'sans-serif', color:'#6a7080', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'5px' }}>Email address</label>
                                <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="your@email.com" required autoComplete="email"
                                    style={{ width:'100%', background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'3px', padding:'9px 12px', fontSize:'14px', fontFamily:'sans-serif', color:'#1e2a3a', outline:'none' }} />
                            </div>
                            <div style={{ marginBottom:'0.5rem' }}>
                                <label style={{ display:'block', fontSize:'11px', fontFamily:'sans-serif', color:'#6a7080', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'5px' }}>Password</label>
                                <input type="password" name="password" value={form.password} onChange={handleChange} placeholder="••••••••" required autoComplete="current-password"
                                    style={{ width:'100%', background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'3px', padding:'9px 12px', fontSize:'14px', fontFamily:'sans-serif', color:'#1e2a3a', outline:'none' }} />
                            </div>
                            <div style={{ textAlign:'right', marginBottom:'1.25rem' }}>
                                <Link to="/forgot-password" state={{ email: form.email }} style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#8b6a3a', textDecoration:'none' }}>Forgot password?</Link>
                            </div>
                            <button type="submit" disabled={loading}
                                style={{ width:'100%', background: loading ? '#6a1212' : '#8b1a1a', color:'#f5f0e8', border:'none', padding:'12px', fontSize:'13px', fontFamily:'sans-serif', letterSpacing:'0.08em', textTransform:'uppercase', cursor: loading ? 'not-allowed' : 'pointer', borderRadius:'3px' }}>
                                {loading ? 'Signing in...' : 'Sign in'}
                            </button>
                        </form>
                    </div>

                    <div style={{ textAlign:'center', marginTop:'1.5rem', fontSize:'13px', fontFamily:'sans-serif', color:'#6a7080' }}>
                        Don't have an account?{' '}
                        <Link to="/register" style={{ color:'#8b1a1a', textDecoration:'none', fontWeight:600 }}>Create one free</Link>
                    </div>
                    <div style={{ textAlign:'center', marginTop:'1.5rem', fontSize:'11px', fontFamily:'sans-serif', color:'#9a9080' }}>
                        No party affiliation. No ads. No selling your data.
                    </div>
                </div>
            </div>
        </>
    );
};

export default LoginPage;
